import { useParams, Link, Navigate } from 'react-router-dom';
import { unitsContent } from '@/data/unitsContent';
import { unitsResources } from '@/data/unitsResources';
import styles from './UnitPage.module.css';

const TUTOR_URL = 'https://notebooklm.google.com/notebook/3fbd69b6-c4d0-4012-92bd-b4240cc5c584';

export const UnitPage: React.FC = () => {
  const { unitNumber } = useParams<{ unitNumber: string }>();
  const number = parseInt(unitNumber || '1', 10);

  const unit = unitsContent.find((u) => u.numero === number);
  const resources = unitsResources.find((r) => r.numero === number);

  if (!unit || number < 1 || number > 15) {
    return <Navigate to="/" replace />;
  }

  const prevUnit = number > 1 ? number - 1 : null;
  const nextUnit = number < 15 ? number + 1 : null;

  return (
    <div className={styles.unitPage}>
      <div className={styles.header}>
        <span className={styles.unitNumber}>Unidad {unit.numero}</span>
        <h2 className={styles.title}>{unit.titulo}</h2>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>📝 Resumen</h3>
        <p className={styles.text}>{unit.resumen}</p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>🎯 Conceptos Clave</h3>
        <div className={styles.concepts}>
          {unit.conceptosClave.map((concepto, index) => (
            <div key={index} className={styles.concept}>
              {concepto.titulo && <h4 className={styles.conceptTitle}>{concepto.titulo}</h4>}
              <p className={styles.conceptDescription}>{concepto.descripcion}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>💡 Ejemplo Aplicado</h3>
        <div className={styles.example}>
          <p className={styles.text}>{unit.ejemploAplicado}</p>
        </div>
      </section>

      {/* Material de Lectura */}
      <section className={styles.materialSection}>
        <h3 className={styles.materialTitle}>📁 Material de Lectura</h3>
        <p className={styles.materialDescription}>
          Descargá el PDF de esta unidad para estudiar offline:
        </p>
        {resources?.pdfUrl ? (
          <a
            href={resources.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.materialButton}
          >
            📚 Descargar PDF Unidad {unit.numero}
          </a>
        ) : (
          <span className={styles.materialButtonDisabled}>
            📚 PDF no disponible aún
          </span>
        )}
      </section>

      {/* Podcast de la Unidad */}
      <section className={styles.podcastSection}>
        <h3 className={styles.podcastTitle}>🎧 Podcast de la Unidad</h3>
        {resources?.spotifyEpisodeId ? (
          <div className={styles.podcastEmbed}>
            <iframe
              style={{ borderRadius: '12px' }}
              src={`https://open.spotify.com/embed/episode/${resources.spotifyEpisodeId}?utm_source=generator`}
              width="100%"
              height="352"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title={`Podcast Unidad ${unit.numero}`}
            />
          </div>
        ) : (
          <p className={styles.podcastPlaceholder}>
            Podcast no disponible aún para esta unidad.
          </p>
        )}
      </section>

      {/* Tutor Digital */}
      <section className={styles.tutorSection}>
        <h3 className={styles.tutorTitle}>🤖 ¿Dudas sobre esta unidad?</h3>
        <p className={styles.tutorDescription}>
          Consultale al tutor digital con IA disponible 24/7. Está entrenado con todo el material de la materia.
        </p>
        <a
          href={TUTOR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.tutorButton}
        >
          Consultar Tutor Digital →
        </a>
      </section>

      <nav className={styles.navigation}>
        {prevUnit && (
          <Link to={`/unidad/${prevUnit}`} className={`${styles.navButton} ${styles.prev}`}>
            ← Unidad {prevUnit}
          </Link>
        )}
        <Link to="/" className={`${styles.navButton} ${styles.home}`}>
          🏠 Inicio
        </Link>
        {nextUnit && (
          <Link to={`/unidad/${nextUnit}`} className={`${styles.navButton} ${styles.next}`}>
            Unidad {nextUnit} →
          </Link>
        )}
      </nav>
    </div>
  );
};
